import { NotificationStateProps, notificationState } from './atoms/notificationAtom';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { UIStateProps, uiState } from './atoms/uiAtom';
import { useAuth, useGetToken, usePostInvalidateToken } from './hooks/auth';
import { useGetAccount, useUpdateAccount } from './hooks/accounts';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import { EanURL } from './common/eanUtils';
import Layout from './components/common/Layout';
import { routes } from './routes';
import { useAcceptTermsAndConditions } from './hooks/termsAndConditions';
import { useEffect } from 'react';
import { useGetNotifications } from './hooks/notifications';
import { useTranslation } from 'react-i18next';
import { userPreferencesState } from './atoms/userPreferencesAtom/userPreferencesAtom';

function Root() {
  const { i18n } = useTranslation();
  const { state } = useLocation();

  const { mutateAsync: fetchToken } = useGetToken();
  const { mutateAsync: acceptTermsAndConditions } = useAcceptTermsAndConditions();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { mutateAsync: updateAccount } = useUpdateAccount();
  const {
    isFetching: isFetchingAccount,
    isError: isErrorAccount,
    refetch: fetchAccount,
  } = useGetAccount({ enabled: false });
  const userPreferenceAtom = useRecoilValue(userPreferencesState);
  const {
    data: notifications,
    isFetching: IsFetchingNotifications,
    isError: isErrorNotifications,
    refetch: refetchNotifications,
  } = useGetNotifications();
  const setUiState = useSetRecoilState<UIStateProps>(uiState);
  const setNotificationState = useSetRecoilState<NotificationStateProps>(notificationState);
  const { mutateAsync: postInvalidateToken } = usePostInvalidateToken();
  useEffect(() => {
    setUiState((state) => ({
      ...state,
      isLoading: isFetchingAccount,
      hasError: isErrorAccount,
    }));
  }, [
    notifications,
    IsFetchingNotifications,
    isErrorNotifications,
    setUiState,
    setNotificationState,
    isFetchingAccount,
    isErrorAccount,
  ]);
  useEffect(() => {
    setUiState((state) => ({
      ...state,
      isLoading: IsFetchingNotifications,
      hasError: isErrorNotifications,
    }));
    if (notifications) {
      setNotificationState((prevState) => ({
        ...prevState,
        items: notifications.data,
      }));
    }
  }, [
    notifications,
    IsFetchingNotifications,
    isErrorNotifications,
    setUiState,
    setNotificationState,
  ]);

  const overviewNavigate = () => {
    if (!userPreferenceAtom.selectedSdpId || !userPreferenceAtom.selectedOverviewMeterTab) {
      navigate(`${routes.OVERVIEW.path}${window.location.search}`);
    } else {
      navigate(
        `${routes.withArgs(
          `${routes.OVERVIEW.path}${routes.OVERVIEW.children.OVERVIEW_DETAIL.path}`,
          {
            ':id': EanURL.encrypt(userPreferenceAtom.selectedSdpId) as string,
            ':tabId': userPreferenceAtom.selectedOverviewMeterTab,
          }
        )}${window.location.search}`
      );
    }
  };

  const updateUserLanguage = (lang: string) => {
    if (!lang) return;
    localStorage.setItem('lang', lang);
    i18n.changeLanguage(lang);
  };

  useEffect(() => {
    setUiState((state) => ({
      ...state,
      isLoading: true,
    }));
    if (!user || state?.isEditProfile || state?.isEditPwd) {
      fetchToken({
        isSignUp: state?.isSignUp,
        isEditProfile: state?.isEditProfile,
        isEditPwd: state?.isEditPwd,
        authCodeParam: state?.authCode,
      })
        .then((tokenResponse) => {
          if (!tokenResponse) {
            navigate(`${routes.SIGN_OUT.path}${window.location.search}`);
          } else {
            if (state?.isSignUp === true) {
              acceptTermsAndConditions()
                .then((response) => {
                  if (response.status === 200) {
                    updateAccount({
                      language: tokenResponse.user!.language,
                    })
                      .then(() => {
                        updateUserLanguage(tokenResponse.user!.language);
                        refetchNotifications().then(() => {
                          overviewNavigate();
                        });
                      })
                      .catch(() => {
                        setUiState((prev) => ({ ...prev, hasError: true }));
                      });
                  }
                })
                .catch(() => {
                  navigate(`${routes.SIGN_OUT.path}${window.location.search}`);
                });
            } else if (state?.isEditProfile === true || state?.isEditPwd === true) {
              updateAccount({ language: tokenResponse.user!.language })
                .then(() => {
                  updateUserLanguage(tokenResponse.user!.language);
                  postInvalidateToken()
                    .then(() => {
                      navigate(`${routes.PROFILE.path}${window.location.search}`);
                    })
                    .catch(() => {
                      setUiState((prev) => ({ ...prev, hasError: true }));
                    });
                })
                .catch(() => {
                  setUiState((prev) => ({ ...prev, hasError: true }));
                });
            } else {
              updateUserLanguage(tokenResponse.user!.language);
              refetchNotifications().then(() => {
                overviewNavigate();
              });
            }
          }
        })
        .catch(() => {
          navigate(`${routes.SIGN_OUT.path}${window.location.search}`);
        });
    } else if (location.pathname === routes.ROOT.path) {
      fetchAccount().then((accountResponse) => {
        updateUserLanguage(accountResponse?.data?.data.language!);
        refetchNotifications().then(() => {
          overviewNavigate();
        });
      });
    } else {
      fetchAccount().then((accountResponse) => {
        updateUserLanguage(accountResponse?.data?.data.language!);
        refetchNotifications();
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Layout>
      <Outlet />
    </Layout>
  );
}

export default Root;
